import { DEFAULT_LOADING_FIGURE, type LoadingFigureSetting } from '../../constants/loadingFigures'
import { DEFAULT_THEME_SETTINGS } from '../../theme/presets'
import { DEFAULT_VIEW_PREFS, type ViewPrefs } from '../../utils/viewPrefs'
import type { CharacterKey } from '../../utils/characterKeys'
import { defaultPageNamingSchemes, type PageNamingScheme } from '../../services/pages/pageNaming'
import { defaultDocumentTemplates, type DocumentTemplate } from '../../services/corpus/documentTemplates'
import { DEFAULT_PAGE_STATUSES } from '../../services/pages/pageStatus'
import { DEFAULT_PAGE_VIEW_DISPLAY } from '../../utils/pageViewDisplay'
import { DEFAULT_LIST_PREFS } from '../../utils/listStyles'
import type { TypewriterMotion } from '../../utils/typewriterMotion'
import type { CaretBlink, CaretShape } from '../../utils/caretStyle'
import { DEFAULT_GOAL_DAYS } from '../../utils/goalModel'
import { DEFAULT_CORPUS_VIEW, type CorpusViewId } from '../../utils/corpusViewChoice'
import { DEFAULT_CALENDAR_PREFS, DEFAULT_DOCKET_PREFS, DEFAULT_GOAL_PREFS, DEFAULT_TOOL_PREFS } from '../../utils/toolSettings'
import { DEFAULT_WRITING_HABIT } from './writingHabit'
import { DEFAULT_LOOSE_END_SETTINGS } from './looseEnds'
import { DEFAULT_EDITOR_MODE_SETTINGS } from './editorMode'
import { DEFAULT_PROOFING_SETTINGS } from './proofing'
import { defaultTypographySettings } from './typography'
import { defaultIconStyles } from './iconStyles'
import { defaultContextMenuItems } from './contextMenu'
import type { ShortcutOverrides } from './normalize'

// How the desktop toolbars and their menus look: set on the page, docked to the window edges, or floating.
export type ToolbarStyle = 'guide' | 'carriage' | 'pill'

/** Which paragraphs break long words at line ends: none, justified ones, or all. */
export type Hyphenation = 'off' | 'justified' | 'all'
export const HYPHENATIONS: readonly Hyphenation[] = ['off', 'justified', 'all']

/** When the tab row shows: never (tabs off), with the top toolbar, or all the time. */
export type TabsVisibility = 'never' | 'hover' | 'always'
export const TABS_VISIBILITIES: readonly TabsVisibility[] = ['never', 'hover', 'always']

/** Where a page opened from the corpus goes: a tab of its own, or the tab in front. */
export type TabOpenMode = 'new' | 'replace'

/** Where the tabs sit: above the top toolbar, on the page above the text column, or in the toolbar's own row. */
export type TabStyle = 'above' | 'page' | 'row'
export const TAB_STYLES: readonly TabStyle[] = ['above', 'page', 'row']

/** What the phone's right edge holds: the side bar, the tools (no side bar), or nothing. */
export type MobileRightEdge = 'bar' | 'tools' | 'off'
export const MOBILE_RIGHT_EDGES: readonly MobileRightEdge[] = ['bar', 'tools', 'off']

/** Every synced setting's default; a saved value replaces it key by key. */
export const defaults = {
  // Per device; see perDevice() in the store.
  desktopWidth: 65,
  desktopWidthUnit: 'ch' as 'px' | 'ch',
  mobileWidth: 65,
  mobileWidthUnit: 'ch' as 'px' | 'ch',
  desktopFontSize: 20,
  mobileFontSize: 18,
  desktopCorpusView: DEFAULT_CORPUS_VIEW as CorpusViewId,
  mobileCorpusView: DEFAULT_CORPUS_VIEW as CorpusViewId,
  fontFamily: "'Besley', Georgia, serif",
  installedFontIds: [] as string[],
  appFontOrder: [] as string[],
  hiddenAppFonts: [] as string[],
  showSystemFonts: true,
  lineHeight: 1.6,
  hyphenation: 'off' as Hyphenation,
  bgColor: '#f0e3c4',
  textColor: '#2b2115',
  theme: { ...DEFAULT_THEME_SETTINGS },
  startupBehavior: 'last-page' as 'last-page' | 'new-page',
  /** The writer's name as exports credit it. File metadata only; unrelated to the account. */
  exportAuthorName: '',
  newPagePlaceholder: 'Start writing…',
  newPagePlaceholderEnabled: true,
  pageNamingSchemes: structuredClone(defaultPageNamingSchemes) as PageNamingScheme[],
  pageNamingAssignments: {} as Record<string, string>,
  documentTemplates: structuredClone(defaultDocumentTemplates) as DocumentTemplate[],
  referenceHighlightingEnabled: false,
  panelMode: 'edge' as 'button' | 'edge',
  hoverTopEnabled: true,
  hoverBottomEnabled: true,
  hoverLeftEnabled: true,
  hoverRightEnabled: true,
  cloudSyncUrl: '',
  cloudSyncApiKey: '',
  cloudSyncAutoSync: false,
  spellcheckEnabled: false,
  proofing: DEFAULT_PROOFING_SETTINGS,
  // Pinned bars and the writer's default page (utils/viewPrefs).
  viewPrefs: DEFAULT_VIEW_PREFS as ViewPrefs,
  shortcutOverrides: {} as ShortcutOverrides,
  // Keys that type characters the keyboard lacks (utils/characterKeys). None until the writer adds one.
  characterKeys: [] as CharacterKey[],
  syncSettingsEnabled: true,
  keyLockPromptStyle: 'modal' as 'modal' | 'indicator',
  // The Lissajous figure the page-loading beam traces (Settings > Appearance).
  loadingFigure: DEFAULT_LOADING_FIGURE as LoadingFigureSetting,
  colorPickerMode: 'basic' as 'basic' | 'advanced',
  wordCountDisplay: 'abbreviated' as 'abbreviated' | 'exact',
  // Width in px of the editor's scroll bar on the desktop layout (Settings > Interface).
  scrollbarWidth: 12,
  writingHabit: DEFAULT_WRITING_HABIT,
  // When a day begins and which day starts the week, for goals, streaks, and the writing log.
  goalDays: DEFAULT_GOAL_DAYS,
  // How the calendar opens and what it shows (Settings > Calendar).
  calendarPrefs: DEFAULT_CALENDAR_PREFS,
  // How goals and streaks are counted and shown, and how sprints run (Settings > Goals & Habits).
  goalPrefs: DEFAULT_GOAL_PREFS,
  // How Docket adds, finishes and shows tasks (Settings > Docket).
  docketPrefs: DEFAULT_DOCKET_PREFS,
  looseEnds: DEFAULT_LOOSE_END_SETTINGS,
  // Shared by the taskbar tools: which appear, dates and times, scope, colors.
  toolPrefs: DEFAULT_TOOL_PREFS,
  editorMode: DEFAULT_EDITOR_MODE_SETTINGS,
  // The page view's surroundings and paper on screen; never part of an export.
  pageViewDisplay: DEFAULT_PAGE_VIEW_DISPLAY,
  // List presets, the default preset for each kind of list, and the task box (utils/listStyles).
  listPrefs: DEFAULT_LIST_PREFS,
  pageStatuses: DEFAULT_PAGE_STATUSES,
  mobileLayout: 'layout2', // 'layout2' (top bar above keyboard, bottom bar in sidebar)
  mobileTopPadding: 15, // Ergonomic top padding on mobile in vh/percent
  rightBarFontWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000],
  selectionToolbarDisplayMode: 'icons', // Display mode for mobile context menu: 'icons' (default) | 'text'
  mobileKeyboardToolbarEnabled: true,
  // The phone's right edge: the side bar, the tools (no side bar), or nothing.
  mobileRightEdge: 'bar' as MobileRightEdge,
  mobileSelectionToolbarEnabled: true,
  radialMenuEnabled: false,
  mobileStaticTopToolbarEnabled: false,
  // Radial menu: 5–10 icons around a center hub, one set for each case.
  radialMenuSelectionItems: [
    'cut',
    'copy',
    'bold',
    'italic',
    'lookupReference',
    'flagLooseEnd'
  ],
  radialMenuEmptyItems: [
    'paste',
    'undo',
    'redo',
    'selectAll',
    'parkIdea',
    'flagLooseEnd'
  ],
  desktopTypewriterModeEnabled: false, // Center-focus active cursor line
  mobileTypewriterModeEnabled: false,
  desktopTypewriterEyeline: 50, // Desktop active line eye-line (50% from bottom of container)
  desktopTypewriterThreshold: 2, // Desktop active line scroll tolerance in px
  mobileTypewriterEyeline: 40, // Mobile active line eye-line (40% from bottom of visible viewport)
  mobileTypewriterThreshold: 2, // Mobile active line scroll tolerance in px
  typewriterMotion: 'drift' as TypewriterMotion, // How the page moves the typing line to the eye-line
  typewriterBailEnabled: false, // A brief guide line at the eye-line after a long return
  // The writing cursor, drawn by the app on the desktop layout (utils/caretStyle).
  caretShape: 'bar' as CaretShape,
  caretBlink: 'steady' as CaretBlink,
  caretHoldWhileTyping: false, // No blinking while keys are being pressed
  caretRestWhenIdle: false, // Blinking stops after 5s with no typing or moving
  clickToTypeEnabled: true, // Allow clicking in empty space below content to start typing there
  downArrowNewLineEnabled: true, // Pressing Down on the last line appends a new paragraph
  rememberPlaceEnabled: true, // Reopened pages put the cursor back where the writer left it
  tableHeaderRowEnabled: true, // New tables start with a header row
  focusDimmingMode: 'off' as 'off' | 'paragraph' | 'sentence' | 'word',
  documentSearchPosition: 'below' as 'above' | 'below', // Find-in-page bar sits a third up ('below') or down ('above') the screen

  // Tabs (desktop): off by default (utils/tabModel, stores/tabs).
  tabsVisibility: 'never' as TabsVisibility,
  tabOpenMode: 'new' as TabOpenMode,
  tabStyle: 'above' as TabStyle,

  // Desktop Toolbar Dimensions
  toolbarStyle: 'guide' as ToolbarStyle,
  topBarRulerEnabled: true, // The column scale under the Carriage toolbar
  topBarThickness: 46,
  topBarIconSize: 28,
  bottomBarThickness: 46,
  bottomBarIconSize: 28,
  // The right bar (the taskbar and the old right toolbar in one) takes the taskbar's size.
  rightBarWidth: 62,
  rightBarKeySize: 44,

  // Mobile Toolbar Dimensions
  mobileKeyboardToolbarThickness: 46,
  mobileKeyboardToolbarIconSize: 32,
  mobileStaticTopToolbarThickness: 52,
  mobileStaticTopToolbarIconSize: 36,
  mobileRightBarThickness: 68,
  mobileRightBarIconSize: 44,
  mobileSelectionToolbarThickness: 42,
  mobileSelectionToolbarIconSize: 38,
  radialMenuRadius: 100,
  radialMenuButtonSize: 48,

  typography: defaultTypographySettings,
  iconStyles: defaultIconStyles,
  contextMenuEnabled: true,
  contextMenuItems: defaultContextMenuItems,
}
